import { DeleteIcon } from '@/components/icons'
import { WorkItemStatusBadge } from '@/components/projects/WorkItemStatusBadge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { Intent } from '@/types'
import { X } from 'lucide-react'

interface BulkDeleteIntentsDialogProps {
  intents: Intent[]
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: () => void
  isPending: boolean
}

function formatIntentNumber(num: number | null): string {
  if (num === null) return '—'
  return `I-${String(num).padStart(3, '0')}`
}

export function BulkDeleteIntentsDialog({
  intents,
  open,
  onOpenChange,
  onConfirm,
  isPending,
}: BulkDeleteIntentsDialogProps) {
  const count = intents.length
  const noun = count === 1 ? 'intent' : 'intents'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[640px] rounded-2xl p-8 gap-6 border-none shadow-elevated" hideCloseButton>
        <DialogHeader className="flex-row items-center justify-between space-y-0">
          <DialogTitle className="text-2xl font-bold leading-[1.3] tracking-[0.015em] text-foreground">
            Delete {count} {noun}?
          </DialogTitle>
          <DialogClose className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none" aria-label="Close">
            <X className="h-6 w-6" />
            <span className="sr-only">Close</span>
          </DialogClose>
        </DialogHeader>
        <DialogDescription className="text-sm font-normal leading-[1.6] tracking-[0.015em] text-foreground">
          {count === 1 ? (
            <>Are you sure you want to delete the intent "<span className="font-bold">{intents[0].name}</span>"? This action cannot be undone.</>
          ) : (
            <>Are you sure you want to delete the selected {count} {noun}? This action cannot be undone.</>
          )}
        </DialogDescription>
        {count > 1 && (
          <div className="overflow-hidden">
            <p className="text-sm font-medium text-foreground mb-2">Selected {noun}:</p>
            <div className="max-h-[240px] overflow-y-auto flex flex-col gap-1">
              {intents.map((intent) => (
                <div
                  key={intent.id}
                  className="flex items-center gap-3 rounded-lg border border-[#E5E7EB] px-3 py-2"
                  data-testid="bulk-delete-intent-row"
                >
                  <span className="w-12 shrink-0 text-sm font-medium text-muted-foreground">
                    {formatIntentNumber(intent.number)}
                  </span>
                  <span className="flex-1 min-w-0 text-sm font-bold text-foreground truncate">
                    "{intent.name}"
                  </span>
                  <span className="shrink-0"><WorkItemStatusBadge status={intent.status} /></span>
                </div>
              ))}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
            className="h-10 min-w-10 rounded-lg py-2.5 px-4 gap-2.5 font-bold"
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={isPending}
            data-testid="bulk-delete-intents-confirm-button"
            className="h-10 min-w-10 rounded-lg py-2.5 px-4 gap-2.5 font-bold [&_svg]:size-6"
          >
            <DeleteIcon className="h-6 w-6" />
            {isPending ? 'Deleting...' : `Delete ${count} ${noun}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
